"use client";

import type { Media } from "@mw/shared/payload-types";

import { RichText } from "@ui/components/core/RichText";
import { useCallback, useState } from "react";

import Accordion from "./AccordionV2";
import BackgroundForegroundImage from "./BackgroundForegroundImage";
import { DottedBorder } from "./GradientBorder";

interface AccordionAsset {
  background?: Media;
  foreground: Media;
  overlay?: Media;
  theme?: "dark" | "light" | "transparent";
}

function AccordionCard({
  open,
  onClick,
  title,
  contentTitle,
  content,
  asset,
  cardKey,
}: {
  asset: AccordionAsset;
  cardKey: string;
  content: string;
  contentTitle: string;
  onClick: () => void;
  open: boolean;
  title: string;
}) {
  return (
    <Accordion onClick={onClick} open={open} title={title}>
      <div className='flex flex-col gap-16 py-20 md:flex-row md:items-start md:gap-64'>
        <div className='order-2 flex basis-3/5 flex-col items-start py-16 md:order-1'>
          <h3 className='f-heading-05 mb-10'>{contentTitle}</h3>
          <RichText size='xs' color='gray' content={content} />
        </div>
        <BackgroundForegroundImage
          animationKey={`${cardKey}-bg-${open}`}
          background={asset.background}
          className='order-1 basis-2/5 md:order-2'
          foreground={asset.foreground}
          overlay={asset.overlay}
          theme={asset.theme}
        />
      </div>
    </Accordion>
  );
}

export interface AccordionCardItem {
  asset: AccordionAsset;
  content: string;
  contentTitle: string;
  key: string;
  title: string;
}

export interface AccordionCardsProps {
  items: AccordionCardItem[];
  title?: null | string;
}

export default function AccordionCards({ items, title }: AccordionCardsProps) {
  const [openKey, setOpenKey] = useState(items[0]?.key || "");

  const handleAccordionClick = useCallback(
    (key: string) => {
      if (openKey === key) return;
      setOpenKey(openKey === key ? "" : key);
    },
    [openKey],
  );

  return (
    <div>
      {title && <h2 className='f-heading-03 mb-100 text-center' dangerouslySetInnerHTML={{ __html: title }} />}

      <div className='relative mx-auto flex w-full flex-col'>
        <DottedBorder side='top' />
        {items.map(item => (
          <AccordionCard
            asset={item.asset}
            cardKey={item.key}
            content={item.content}
            contentTitle={item.contentTitle}
            key={item.key}
            onClick={() => handleAccordionClick(item.key)}
            open={openKey === item.key}
            title={item.title}
          />
        ))}
      </div>
    </div>
  );
}
